import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Users, Wallet, AlertCircle, TrendingUp, AlertTriangle, TrendingDown, Gauge, UserPlus, Package, Mail, X } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { TYPE_BY_VALUE, benchmarkFor } from "../lib/propertyTypes";
import PortfolioMap from "../components/PortfolioMap";
import RecentActivityWidget from "../components/RecentActivityWidget";
import { CreateAccountDialog } from "../components/CreateAccountDialog";
import AdminRecentAccounts from "../components/AdminRecentAccounts";
import InterestRatesWidget from "../components/InterestRatesWidget";
import PortalEngagementWidget from "../components/PortalEngagementWidget";
import LenderPackageButton from "../components/LenderPackageButton";
import LenderPipelineWidget from "../components/LenderPipelineWidget";
import UpcomingScheduleWidget from "../components/UpcomingScheduleWidget";
import RetentionSavesWidget from "../components/RetentionSavesWidget";
import HospitalityPulseWidget from "../components/HospitalityPulseWidget";
import CancelReasonsBreakdown from "../components/CancelReasonsBreakdown";
import AdminRevenueSummary from "../components/AdminRevenueSummary";

const currency = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const LOW_CAP_THRESHOLD = 5; // %

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [properties, setProperties] = useState([]);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [createdSignal, setCreatedSignal] = useState(0);
  // BYO SMTP onboarding nudge state. null = unknown / still loading,
  // {} = no account configured, {...account} = configured.
  const [smtpAccount, setSmtpAccount] = useState(null);
  const [smtpNudgeDismissed, setSmtpNudgeDismissed] = useState(() => {
    try {
      const ts = window.localStorage.getItem("rvm_smtp_nudge_dismissed_until");
      return ts ? Number(ts) > Date.now() : false;
    } catch {
      return false;
    }
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch((e) => console.warn("stats load failed", e));
    api.get("/loans/alerts").then((r) => setAlerts(r.data)).catch((e) => console.warn("alerts load failed", e));
    api.get("/properties").then((r) => setProperties(r.data || [])).catch((e) => console.warn("properties load failed", e));
    // Only fetch SMTP state for landlords; admins don't send relationship
    // emails so the nudge doesn't apply to them.
    if (!isAdmin) {
      api.get("/email-account").then((r) => setSmtpAccount(r.data?.account || {})).catch(() => setSmtpAccount({}));
    }
  }, [isAdmin]);

  const dismissSmtpNudge = () => {
    // Snooze for 14 days so the banner isn't naggy.
    try {
      window.localStorage.setItem(
        "rvm_smtp_nudge_dismissed_until",
        String(Date.now() + 14 * 24 * 3600 * 1000),
      );
    } catch { /* localStorage unavailable — accept session-only dismiss */ }
    setSmtpNudgeDismissed(true);
  };

  // Show the nudge when we know the landlord has NO enabled SMTP account
  // configured and they haven't dismissed it recently. The endpoint returns
  // `{ account: null }` when nothing is set up, or a full account dict when
  // it is — so we gate on host + enabled to catch both new accounts and
  // disabled-but-still-present ones.
  const hasWorkingSmtp = Boolean(smtpAccount?.host) && smtpAccount?.enabled !== false;
  const showSmtpNudge =
    !isAdmin
    && smtpAccount !== null
    && !hasWorkingSmtp
    && !smtpNudgeDismissed;

  const isSubscribed = user?.subscription_status === "active";

  const watchlist = properties
    .filter((p) => typeof p.cap_rate === "number" && p.cap_rate < LOW_CAP_THRESHOLD)
    .sort((a, b) => (a.cap_rate ?? 0) - (b.cap_rate ?? 0))
    .slice(0, 8);

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 md:p-8">
        {!isSubscribed && (
          <div className="mb-6 border border-amber-300 bg-amber-50 rounded-sm p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-testid="subscription-banner">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-slate-900">Activate your subscription</div>
                <div className="text-sm text-slate-600">Pick a plan to unlock full portfolio management and tenant billing.</div>
              </div>
            </div>
            <Link to="/dashboard/billing" data-testid="go-billing-btn" className="btn-primary-dark text-sm shrink-0">Upgrade →</Link>
          </div>
        )}

        {showSmtpNudge && (
          <div
            className="mb-6 border border-sky-200 bg-sky-50 rounded-sm p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
            data-testid="smtp-nudge-banner"
          >
            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-slate-900 flex items-center gap-2">
                  Send investor &amp; tenant emails from <em>your own</em> mailbox
                </div>
                <div className="text-sm text-slate-600 mt-1 leading-relaxed">
                  By default, your rent reminders, monthly investor updates and lender deliveries are sent
                  from RealtyVault. Connect your own email (Gmail, Microsoft 365, Outlook, or any SMTP server) and
                  every relationship message will come from <b>{user?.email || "your address"}</b> — replies land in
                  your inbox and your domain's reputation does the work.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              <Link
                to="/dashboard/settings#email-account"
                data-testid="smtp-nudge-connect"
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm bg-sky-700 text-white hover:bg-sky-800 transition-colors"
              >
                Connect email
              </Link>
              <button
                type="button"
                onClick={dismissSmtpNudge}
                data-testid="smtp-nudge-dismiss"
                title="Hide for 14 days"
                className="p-1.5 rounded-sm text-slate-500 hover:text-slate-900 hover:bg-sky-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <header className="flex flex-col sm:flex-row sm:items-start justify-between mb-8 gap-4">
          <div className="flex items-start gap-4 min-w-0">
            {user?.logo_data_uri && (
              <img
                src={user.logo_data_uri}
                alt={user?.company || "Company logo"}
                className="w-14 h-14 object-contain rounded-sm border border-slate-200 bg-white p-1 shrink-0"
                data-testid="dashboard-company-logo"
              />
            )}
            <div className="min-w-0">
              <div className="overline text-slate-500" data-testid="dashboard-company-overline">
                {user?.company ? user.company : "Owner Console"}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1" style={{fontFamily:"Outfit"}}>
                Welcome back, {user?.full_name?.split(" ")[0]}.
              </h1>
              <p className="text-sm text-slate-500 mt-1">Here's what's happening across your portfolio today.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          </div>
        </header>

        {isAdmin && <AdminRevenueSummary />}

        {isAdmin && (
          <div className="mb-8 bg-white border border-slate-200 rounded-sm p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-slate-900 hover:shadow-sm transition" data-testid="admin-create-account-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-slate-900 text-white flex items-center justify-center shrink-0">
                <UserPlus className="w-5 h-5" />
              </div>
              <div>
                <div className="overline text-slate-500">Platform Admin</div>
                <div className="text-lg font-bold text-slate-900 tracking-tight" style={{fontFamily:"Outfit"}}>Create new account</div>
                <div className="text-sm text-slate-500 mt-0.5">Onboard an investor, tenant, or admin. A welcome email with login credentials is sent automatically.</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCreateAccountOpen(true)}
              className="btn-primary-dark flex items-center gap-2 text-sm shrink-0"
              data-testid="admin-create-account-btn"
            >
              <UserPlus className="w-4 h-4" /> Create account
            </button>
          </div>
        )}

        <CreateAccountDialog
          open={createAccountOpen}
          onClose={() => setCreateAccountOpen(false)}
          onCreated={() => {
            // Refresh the tenant count KPI so admins see new activity immediately
            api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
            // Bump the recent-accounts widget to reload
            setCreatedSignal((n) => n + 1);
          }}
        />

        {isAdmin && (
          <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-retention-wrap">
            <div className="lg:col-span-2">
              <RetentionSavesWidget />
            </div>
            <div className="lg:col-span-1">
              <CancelReasonsBreakdown />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mb-8" data-testid="admin-recent-accounts-wrap">
            <AdminRecentAccounts refreshSignal={createdSignal} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 stagger-in">
          <StatCard label="Portfolio Value" value={currency(stats?.portfolio_value)} icon={TrendingUp} testid="stat-portfolio-value" bgClass="bg-green-50 border-green-200" />
          <StatCard label="Monthly Rent Roll" value={currency(stats?.monthly_rent_roll)} icon={Wallet} testid="stat-rent-roll" bgClass="bg-green-50 border-green-200" />
          <StatCard label="Properties" value={stats?.properties ?? 0} icon={Building2} testid="stat-properties" bgClass="bg-yellow-50 border-yellow-200" />
          <StatCard label="Tenants" value={stats?.tenants ?? 0} icon={Users} testid="stat-tenants" bgClass="bg-blue-50 border-blue-200" />
        </div>

        {/* Lender package quick-start card — landlords only (admins don't manage portfolios) */}
        {!isAdmin && (
          <div className="mt-6 bg-purple-50 border border-purple-200 rounded-sm p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-purple-400 hover:shadow-sm transition" data-testid="dashboard-lender-package-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-purple-600 text-white flex items-center justify-center shrink-0">
                <Package className="w-5 h-5" />
              </div>
              <div>
                <div className="overline text-purple-700">Send your portfolio to a lender</div>
                <div className="text-lg font-bold text-slate-900 tracking-tight" style={{fontFamily:"Outfit"}}>One-click underwriting package</div>
                <div className="text-sm text-slate-600 mt-0.5">Bundle your rent roll, PFS, lease binder, and comparable sales into a single ZIP — or password-gated share link — your lender can open in seconds.</div>
              </div>
            </div>
            <LenderPackageButton variant="primary" testid="dashboard-lender-package-btn" label="Generate" />
          </div>
        )}

        {alerts && alerts.total > 0 && (
          <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="dashboard-maturity-alerts">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-bold text-slate-900">Loan Maturity Alerts</h3>
                <span className="text-xs text-slate-400 font-mono">({alerts.total} in next 90 days)</span>
              </div>
              <Link to="/dashboard/loans" data-testid="view-all-alerts" className="text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors">View all →</Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <AlertPill sev="red" label="≤ 30 days" count={alerts.counts.red} />
              <AlertPill sev="yellow" label="≤ 60 days" count={alerts.counts.yellow} />
              <AlertPill sev="green" label="≤ 90 days" count={alerts.counts.green} />
            </div>
          </div>
        )}

        {/* Low Cap-Rate Watchlist */}
        {watchlist.length > 0 && (
          <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="dashboard-cap-watchlist">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-cyan-600" />
                <h3 className="text-sm font-bold text-slate-900">Low Cap-Rate Watchlist</h3>
                <span className="text-xs text-slate-400 font-mono">({watchlist.length} below {LOW_CAP_THRESHOLD}%)</span>
              </div>
              <Link to="/dashboard/properties" data-testid="view-all-cap" className="text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors">View portfolio →</Link>
            </div>
            <div className="text-[11px] text-slate-500 mb-3 flex items-start gap-2">
              <TrendingDown className="w-3 h-3 mt-0.5 shrink-0 text-cyan-600" />
              <span>Assets with sub-{LOW_CAP_THRESHOLD}% yield. Consider rent escalation, refinance, expense audit, or repositioning.</span>
            </div>
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {watchlist.map((p) => {
                const cap = p.cap_rate;
                const typeMeta = TYPE_BY_VALUE[p.property_type];
                const bench = benchmarkFor(p.property_type, cap);
                // Combined severity: hard-range (<3 red, <4 amber, else cyan) OR benchmark signals "below"
                const hardSev = cap < 3 ? "red" : cap < 4 ? "amber" : "cyan";
                const sev = bench.status === "below" && hardSev === "cyan" ? "amber" : hardSev;
                const dot = sev === "red" ? "bg-red-500" : sev === "amber" ? "bg-amber-500" : "bg-cyan-500";
                const txt = sev === "red" ? "text-red-700" : sev === "amber" ? "text-amber-700" : "text-cyan-700";
                const typeLabel = (typeMeta?.label || p.property_type || "").toUpperCase();
                return (
                  <div key={p.id} className="flex items-center justify-between py-2.5" data-testid={`watchlist-row-${p.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                          <span aria-hidden="true">{typeMeta?.emoji || "📦"}</span>
                          <span>{p.name}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {p.address} · <span className="uppercase tracking-widest">{typeLabel}</span>
                          {bench.status === "below" && bench.min != null && (
                            <> · <span className="text-amber-700 font-semibold" data-testid={`watchlist-benchmark-${p.id}`}>
                              Typical {bench.min}–{bench.max}% — you're below
                            </span></>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 shrink-0">
                      <div className="hidden sm:block text-right">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">NOI</div>
                        <div className="text-xs font-mono text-slate-700">{currency(p.noi)}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-mono font-bold ${txt}`}>{cap.toFixed(2)}%</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">cap rate</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Portfolio map — landlords only (admins don't have an aggregate property set) */}
        {!isAdmin && (
          <div className="mt-6" data-testid="dashboard-portfolio-map">
            <PortfolioMap properties={properties} height={420} />
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-slate-500">Collections</div>
                <h3 className="text-lg font-bold tracking-tight text-slate-900">Collected vs. Expected</h3>
              </div>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.chart || []}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{ borderRadius: 2, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v) => `$${v.toLocaleString()}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="expected" name="Expected" fill="#CBD5E1" />
                  <Bar dataKey="collected" name="Collected" fill="#0F172A" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="overline text-slate-500 mb-1">Key Metrics</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900">Financial Snapshot</h3>
            <dl className="mt-6 space-y-4">
              <Metric label="Total Collected" value={currency(stats?.total_collected)} />
              <Metric label="Outstanding" value={currency(stats?.outstanding)} negative={stats?.outstanding > 0} />
              <Metric label="Expenses" value={currency(stats?.total_expenses)} />
              <Metric label="Active Leases" value={stats?.active_leases ?? 0} />
            </dl>
          </div>
        </div>

        {/* Recent activity feed (investor only) */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <RecentActivityWidget limit={10} />
          </div>
          <div className="space-y-4">
            <div data-testid="dashboard-interest-rates">
              <InterestRatesWidget />
            </div>
            {!isAdmin && <UpcomingScheduleWidget />}
            {!isAdmin && <HospitalityPulseWidget />}
            {!isAdmin && <PortalEngagementWidget />}
            {!isAdmin && <LenderPipelineWidget />}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function StatCard({ label, value, icon: Icon, testid, bgClass = "bg-white border-slate-200" }) {
  return (
    <div className={`${bgClass} border rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-slate-500">{label}</div>
          <div className="mt-3 text-3xl font-bold tracking-tight text-slate-900 font-mono">{value}</div>
        </div>
        <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm">
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, negative }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className={`font-mono text-lg font-bold ${negative ? "text-red-600" : "text-slate-900"}`}>{value}</dd>
    </div>
  );
}

function AlertPill({ sev, label, count }) {
  const styles = {
    red: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", dot: "bg-red-500" },
    yellow: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", dot: "bg-amber-500" },
    green: { bg: "bg-green-50", border: "border-green-300", text: "text-green-700", dot: "bg-green-500" },
  }[sev];
  const active = count > 0;
  return (
    <div data-testid={`dashboard-alert-pill-${sev}`} className={`rounded-sm border p-3 flex items-center justify-between ${active ? `${styles.bg} ${styles.border}` : "bg-slate-50 border-slate-200"}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${styles.dot}`}></span>
        <span className={`overline ${active ? styles.text : "text-slate-400"}`}>{label}</span>
      </div>
      <span className={`font-mono text-xl font-bold ${active ? styles.text : "text-slate-400"}`}>{count}</span>
    </div>
  );
}
